// components/LotComponents.jsx - Updated with DefectTypeAnalysis and Pagination
import React, { useState, useEffect } from 'react';
import { useAppContext } from '../context/AppContext.jsx'; // 1. Import useAppContext
import { LoadingSpinner } from './ui/LoadingSpinner.jsx';
import { TrendChart } from './charts/TrendChart.jsx';
import { InspectionTable } from './InspectionComponents.jsx';
import { DefectTypeAnalysis } from './DefectTypeAnalysis.jsx';
import { api, HEATMAP_CONSTANTS } from '../api/reportingApi.js';

/**
 * Lot summary card shown in lists.
 */
export const LotCard = ({ 
    lot, 
    onViewDetails, 
    showActions = true,
    cardStyle = "row" // "row" for horizontal layout, "card" for vertical card layout
}) => {
    const defectRateColor = lot.defect_rate > 5 ? 'text-danger' : 'text-success';
    
    if (cardStyle === "card") {
        // Vertical card layout (for grid views)
        return (
            <div className="col-md-6 col-lg-4 mb-3">
                <div className="card h-100 shadow-sm border-0">
                    <div className="card-body d-flex flex-column">
                        <div className="d-flex justify-content-between align-items-start mb-3">
                            <div>
                                <h5 className="card-title mb-1">
                                    <i className="fas fa-boxes text-primary me-2" />
                                    {lot.lot_identifier}
                                </h5>
                                <p className="text-muted small mb-0">{lot.project_name}</p>
                            </div>
                        </div>

                        {/* Performance Metrics */}
                        <div className="row text-center mb-3">
                            <div className="col-6 border-end">
                                <h6 className="mb-0 text-primary">{lot.total_inspections}</h6>
                                <small className="text-muted">Inspections</small>
                            </div>
                            <div className="col-6">
                                <h6 className={`mb-0 ${defectRateColor}`}>
                                    {lot.defect_rate.toFixed(1)}%
                                </h6>
                                <small className="text-muted">Defect Rate</small>
                            </div>
                        </div>

                        {/* Additional info */}
                        <div className="mb-3">
                            <p className="text-muted small mb-1">
                                <i className="fas fa-calendar me-1"></i>
                                First: {new Date(lot.first_run_at).toLocaleDateString()}
                            </p>
                            <p className="text-muted small mb-0">
                                <i className="fas fa-clock me-1"></i>
                                Last: {new Date(lot.last_run_at).toLocaleDateString()}
                            </p>
                        </div>

                        {showActions && (
                            <div className="mt-auto">
                                <button 
                                    className="btn btn-primary btn-sm w-100" 
                                    onClick={() => onViewDetails(lot)}
                                    title="View detailed analysis"
                                >
                                    <i className="fas fa-info-circle me-1"></i>Details
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }
    
    // Default horizontal row layout
    return (
        <div className="card shadow-sm mb-3 border-0">
            <div className="card-body">
                <div className="row align-items-center">
                    <div className="col-md-6">
                        <div className="d-flex align-items-center mb-2">
                            <i className="fas fa-boxes text-primary me-2" />
                            <h6 className="card-title mb-0">{lot.lot_identifier}</h6>
                        </div>
                        <p className="text-muted small mb-1">
                            <i className="fas fa-project-diagram me-1" />
                            {lot.project_name}
                        </p>
                        <p className="text-muted small mb-1">
                            <i className="fas fa-calendar me-1" />
                            First: {new Date(lot.first_run_at).toLocaleDateString()}
                        </p>
                        <p className="text-muted small mb-0">
                            <i className="fas fa-clock me-1" />
                            Last: {new Date(lot.last_run_at).toLocaleDateString()}
                        </p>
                    </div>

                    <div className="col-md-4">
                        <div className="row text-center">
                            <div className="col-6">
                                <h6 className="mb-0 text-primary">{lot.total_inspections}</h6>
                                <small className="text-muted">Inspections</small>
                            </div>
                            <div className="col-6">
                                <h6 className={`mb-0 ${defectRateColor}`}>
                                    {lot.defect_rate.toFixed(1)}%
                                </h6>
                                <small className="text-muted">Defect Rate</small>
                            </div>
                        </div>
                    </div>

                    {showActions && (
                        <div className="col-md-2 text-end">
                            <button 
                                className="btn btn-primary btn-sm" 
                                onClick={() => onViewDetails(lot)}
                                title="View detailed analysis"
                            >
                                <i className="fas fa-info-circle me-1"></i>Details
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

/**
 * Table row version of lot card (for table layouts like in LotsTrendsTab)
 */
export const LotTableRow = ({ lot, onViewDetails, showTrendButton = false, onViewTrend }) => {
    const defectRateColor = lot.defect_rate > 5 ? 'text-danger' : 'text-success';
    
    return (
        <tr>
            <td><strong>{lot.lot_identifier}</strong></td>
            <td>{lot.project_name}</td>
            <td>{new Date(lot.last_run_at).toLocaleString()}</td>
            <td>{lot.total_inspections}</td>
            <td>
                <span className={`fw-bold ${defectRateColor}`}>
                    {lot.defect_rate.toFixed(1)}%
                </span>
            </td>
            <td>
                <div className="btn-group" role="group">
                    {showTrendButton && onViewTrend && (
                        <button 
                            className="btn btn-primary btn-sm" 
                            onClick={() => onViewTrend(lot)}
                            title="View trend chart"
                        >
                            <i className="fas fa-chart-line me-1"></i>Trend
                        </button>
                    )}
                    <button 
                        className="btn btn-outline-primary btn-sm" 
                        onClick={() => onViewDetails(lot)}
                        title="View detailed analysis"
                    >
                        <i className="fas fa-info-circle me-1"></i>Details
                    </button>
                </div>
            </td>
        </tr>
    );
};

/**
 * Heatmap aggregation component for a specific Lot.
 */
const LotHeatmapAggregation = ({ lot, addNotification, filters }) => {
    // FIXED: Get configuration from context
    const { state, withManualImagesPref } = useAppContext();
    
    const [availableModels, setAvailableModels] = useState([]);
    const [selectedModel, setSelectedModel] = useState('');
    const [aggregationMethod, setAggregationMethod] = useState('average');
    const [isLoading, setIsLoading] = useState(false);
    const [result, setResult] = useState(null);

    const lotId = lot?.lot_identifier;
    const project = lot?.project_name;

    useEffect(() => {
        if (lotId && project) {
            loadAvailableModels();
        }
    }, [lotId, project, state.preferences?.includeManualImages]); // FIXED: Add dependency

    const loadAvailableModels = async () => {
        try {
            // FIXED: Apply configuration preferences to filters
            const baseFilters = { lot_id: lotId, project: project };
            const configuredFilters = withManualImagesPref ? withManualImagesPref(baseFilters) : baseFilters;
            
            const models = await api.getAvailableHeatmapModels(configuredFilters);
            setAvailableModels(models || []);
            if (models && models.length > 0) {
                setSelectedModel(models[0]);
            }
        } catch (error) {
            console.error('Error loading heatmap models for lot:', error);
            addNotification({type: 'warning', message: 'Could not load heatmap models.'});
        }
    };

    const handleGenerateHeatmap = async () => {
        if (!selectedModel) return;

        try {
            setIsLoading(true);
            
            // FIXED: Apply configuration preferences to generation filters
            // const baseFilters = { lot_id: lotId, project: project };
            // const configuredFilters = withManualImagesPref ? withManualImagesPref(baseFilters) : baseFilters;
            
            // const heatmapResult = await api.generateAggregatedHeatmap(
            //     selectedModel,
            //     aggregationMethod,
            //     { useCache: true },
            //     configuredFilters
            // );
            const baseFilters = { 
                lot_id: lotId, 
                project: project,
                date_range: filters?.dateRange || 'all' // Use filter's date range, fallback to 'all'
            };
            const configuredFilters = withManualImagesPref ? withManualImagesPref(baseFilters) : baseFilters;
            
            const heatmapResult = await api.generateAggregatedHeatmap(
                selectedModel,
                aggregationMethod,
                { useCache: true },
                configuredFilters
            );


            setResult(heatmapResult);
            addNotification({
                type: 'success', 
                message: `Generated heatmap from ${heatmapResult.statistics.total_inspections} inspections`
            });
        } catch (error) {
            console.error('Error generating lot heatmap:', error);
            addNotification({type: 'danger', message: `Failed to generate heatmap: ${error.message}`});
        } finally {
            setIsLoading(false);
        }
    };

    const handleDownload = () => {
        if (!result?.aggregated_heatmap) return;
        try {
            const link = document.createElement('a');
            link.href = result.aggregated_heatmap;
            link.download = `lot_${lotId}_${selectedModel}_${aggregationMethod}_heatmap.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            addNotification({type: 'success', message: 'Heatmap downloaded successfully'});
        } catch (error) {
            console.error("Download failed", error);
            addNotification({type: 'danger', message: `Failed to download heatmap: ${error.message}`});
        }
    };

    if (availableModels.length === 0) {
        return (
            <div className="card mb-4">
                <div className="card-body text-center text-muted p-4">
                    <i className="fas fa-fire-extinguisher fa-2x mb-2"></i>
                    <p>No heatmap data available for this lot.</p>
                    {/* FIXED: Add info message about excluded manual images */}
                    {state.preferences?.includeManualImages === false && (
                        <small className="text-info">
                            <i className="fas fa-info-circle me-1"></i>
                            Manual images are excluded. Check Configuration tab to include them.
                        </small>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="card mb-4">
            <div className="card-header">
                <h6 className="mb-0">
                    <i className="fas fa-fire me-2" />
                    Generate Lot Heatmap
                </h6>
            </div>
            <div className="card-body">
                <div className="row mb-3">
                    <div className="col-md-6">
                        <label className="form-label small fw-bold">Model</label>
                        <select 
                            className="form-select" 
                            value={selectedModel} 
                            onChange={(e) => setSelectedModel(e.target.value)}
                            disabled={isLoading}
                        >
                            {availableModels.map(model => (
                                <option key={model} value={model}>{model}</option>
                            ))}
                        </select>
                    </div>
                    <div className="col-md-6">
                        <label className="form-label small fw-bold">Method</label>
                        <select 
                            className="form-select" 
                            value={aggregationMethod}
                            onChange={(e) => setAggregationMethod(e.target.value)}
                            disabled={isLoading}
                        >
                            {HEATMAP_CONSTANTS.AGGREGATION_METHODS.map(method => (
                                <option key={method.value} value={method.value}>
                                    {method.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="d-flex gap-2 mb-3">
                    <button 
                        className="btn btn-primary"
                        onClick={handleGenerateHeatmap}
                        disabled={!selectedModel || isLoading}
                    >
                        {isLoading ? (
                            <>
                                <div className="spinner-border spinner-border-sm me-2" />
                                Generating...
                            </>
                        ) : (
                            <>
                                <i className="fas fa-magic me-2" />
                                Generate Heatmap
                            </>
                        )}
                    </button>

                    {result && (
                        <button className="btn btn-outline-success" onClick={handleDownload}>
                            <i className="fas fa-download me-2" />
                            Download
                        </button>
                    )}
                </div>

                {result && (
                    <div className="border rounded p-3 bg-light">
                        <div className="text-center mb-3">
                            <img 
                                src={result.aggregated_heatmap} 
                                alt="Lot Aggregated Heatmap"
                                className="img-fluid rounded"
                                style={{ maxHeight: '400px' }}
                            />
                        </div>
                        <div className="text-center">
                            <small className="text-muted">
                                Aggregated from {result.statistics.total_inspections} inspections using '{result.statistics.aggregation_method}' method.
                            </small>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

/**
 * Detailed modal for lot analysis and inspections.
 */
export const LotDetailsModal = ({ 
    lot, 
    isOpen, 
    onClose, 
    trendData, 
    isTrendLoading, 
    recentInspections, 
    isInspectionsLoading,
    onViewInspectionDetails,
    onImageClick,
    onSetGroundTruth,
    isGroundTruthLoading,
    addNotification,
    showSummaryCards = true,
    inspectionPagination, 
    onInspectionPageChange,
    modelList,
    filters
}) => {
    // FIXED: Get configuration from context
    const { state } = useAppContext();
    
    if (!isOpen || !lot) return null;

    const defectRateColor = lot.defect_rate > 10 ? 'bg-danger' : 
                           lot.defect_rate > 5 ? 'bg-warning' : 'bg-success';

    // FIXED: Apply configuration preferences to analysis filters
    const analysisFilters = { 
        lot_id: lot.lot_identifier, 
        project: lot.project_name, 
        date_range: 'all',
        // Include manual images preference if available
        ...(state.preferences?.includeManualImages !== undefined && {
            includeManualImages: state.preferences.includeManualImages
        })
    };

    return (
        <div className={`modal fade ${isOpen ? 'show d-block' : ''}`} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-xl modal-dialog-scrollable">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">
                            <i className="fas fa-boxes me-2"></i>
                            Lot {lot.lot_identifier} - Detailed Analysis
                        </h5>
                        <button type="button" className="btn-close" onClick={onClose}></button>
                    </div>
                    <div className="modal-body">
                        
                        {/* FIXED: Add configuration status display */}
                        <div className="alert alert-info py-2">
                            <i className="fas fa-cog me-2"></i>
                            <small>
                                Configuration: Manual images {state.preferences?.includeManualImages ? 'included' : 'excluded'}
                                {state.preferences?.show_confidence_scores !== undefined && 
                                    ` • Confidence scores ${state.preferences.show_confidence_scores ? 'shown' : 'hidden'}`
                                }
                            </small>
                        </div>

                        {/* Lot Summary Cards - Optional */}
                        {showSummaryCards && (
                            <div className="row mb-4">
                                <div className="col-md-3">
                                    <div className="card bg-primary text-white">
                                        <div className="card-body text-center">
                                            <i className="fas fa-microscope fa-2x mb-2"></i>
                                            <h4>{lot.total_inspections.toLocaleString()}</h4>
                                            <small>Total Inspections</small>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="card bg-info text-white">
                                        <div className="card-body text-center">
                                            <i className="fas fa-project-diagram fa-2x mb-2"></i>
                                            <h4 className="small">{lot.project_name}</h4>
                                            <small>Part</small>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className={`card ${defectRateColor} text-white`}>
                                        <div className="card-body text-center">
                                            <i className="fas fa-percentage fa-2x mb-2"></i>
                                            <h4>{lot.defect_rate.toFixed(1)}%</h4>
                                            <small>Defect Rate</small>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="card bg-secondary text-white">
                                        <div className="card-body text-center">
                                            <i className="fas fa-calendar fa-2x mb-2"></i>
                                            <h4 className="small">
                                                {Math.ceil((new Date(lot.last_run_at) - new Date(lot.first_run_at)) / (1000 * 60 * 60 * 24))}
                                            </h4>
                                            <small>Days Active</small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Lot Information */}
                        <div className="row mb-4">
                            <div className="col-md-6">
                                <div className="card">
                                    <div className="card-header">
                                        <h6 className="mb-0">Lot Information</h6>
                                    </div>
                                    <div className="card-body">
                                        <table className="table table-sm mb-0">
                                            <tbody>
                                                <tr>
                                                    <td><strong>Lot ID:</strong></td>
                                                    <td>{lot.lot_identifier}</td>
                                                </tr>
                                                <tr>
                                                    <td><strong>Part:</strong></td>
                                                    <td>{lot.project_name}</td>
                                                </tr>
                                                <tr>
                                                    <td><strong>First Inspection:</strong></td>
                                                    <td>{new Date(lot.first_run_at).toLocaleString()}</td>
                                                </tr>
                                                <tr>
                                                    <td><strong>Last Inspection:</strong></td>
                                                    <td>{new Date(lot.last_run_at).toLocaleString()}</td>
                                                </tr>
                                                <tr>
                                                    <td><strong>Inspection Frequency:</strong></td>
                                                    <td>
                                                        {lot.total_inspections > 1 ? 
                                                            `${(lot.total_inspections / Math.max(1, Math.ceil((new Date(lot.last_run_at) - new Date(lot.first_run_at)) / (1000 * 60 * 60 * 24)))).toFixed(1)} per day` :
                                                            'Single inspection'
                                                        }
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="card">
                                    <div className="card-header">
                                        <h6 className="mb-0">Quality Metrics</h6>
                                    </div>
                                    <div className="card-body">
                                        <div className="mb-3">
                                            <div className="d-flex justify-content-between">
                                                <span>Defect Rate</span>
                                                <span className={`fw-bold ${lot.defect_rate > 5 ? 'text-danger' : 'text-success'}`}>
                                                    {lot.defect_rate.toFixed(1)}%
                                                </span>
                                            </div>
                                            <div className="progress mt-1">
                                                <div 
                                                    className={`progress-bar ${
                                                        lot.defect_rate > 10 ? 'bg-danger' :
                                                        lot.defect_rate > 5 ? 'bg-warning' : 'bg-success'
                                                    }`}
                                                    style={{ width: `${Math.min(lot.defect_rate, 100)}%` }}
                                                ></div>
                                            </div>
                                        </div>
                                        <div className="mb-3">
                                            <div className="d-flex justify-content-between">
                                                <span>Success Rate</span>
                                                <span className="fw-bold text-success">
                                                    {(100 - lot.defect_rate).toFixed(1)}%
                                                </span>
                                            </div>
                                        </div>
                                        <div className="mb-3">
                                            <div className="d-flex justify-content-between">
                                                <span>Total Defects Found</span>
                                                <span className="fw-bold text-danger">
                                                    {Math.round(lot.total_inspections * lot.defect_rate / 100)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Trend Chart */}
                        <div className="card mb-4">
                            <div className="card-header">
                                <h6 className="mb-0">
                                    <i className="fas fa-chart-line me-2"></i>Quality Trend
                                </h6>
                            </div>
                            <div className="card-body">
                                {isTrendLoading ? (
                                    <div className="text-center py-4">
                                        <LoadingSpinner message="Loading trend data..." />
                                    </div>
                                ) : trendData && trendData.length > 0 ? (
                                    <div style={{ height: '300px' }}>
                                        <TrendChart 
                                            chartId={`lot-detail-trend-${lot.lot_identifier}`} 
                                            data={trendData} 
                                        />
                                    </div>
                                ) : (
                                    <div className="text-center text-muted p-4">
                                        <i className="fas fa-chart-line fa-3x mb-3"></i>
                                        <h6>No Trend Data Available</h6>
                                        <p className="small">Trend data requires multiple inspections over time.</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* DEFECT TYPE ANALYSIS - **FIXED RENDER CONDITION** */}
                        {lot.total_inspections > 0 && (
                            <div className="mb-4">
                                <DefectTypeAnalysis 
                                    filters={analysisFilters}
                                    title={`Defect Type Analysis for Lot ${lot.lot_identifier}`}
                                    addNotification={addNotification}
                                    modelList={modelList}
                                    compactMode={true}
                                    showTable={false}
                                />
                            </div>
                        )}
                        
                        {/* Lot Heatmap Aggregation */}
                        {/* <LotHeatmapAggregation lot={lot} addNotification={addNotification} /> */}
                        <LotHeatmapAggregation lot={lot} addNotification={addNotification} filters={filters} />

                        {/* Recent Inspections */}
                        <div className="row">
                            <div className="col-12">
                                {isInspectionsLoading && (!recentInspections || recentInspections.length === 0) ? (
                                    <div className="text-center py-5">
                                        <LoadingSpinner message="Loading recent inspections..." />
                                    </div>
                                ) : (
                                    <>
                                        <InspectionTable
                                            inspections={recentInspections}
                                            onViewDetails={onViewInspectionDetails}
                                            onImageClick={onImageClick}
                                            onSetGroundTruth={onSetGroundTruth}
                                            isLoading={isGroundTruthLoading || isInspectionsLoading}
                                            showExportButtons={false}
                                            title={`Recent Inspections`}
                                            addNotification={addNotification}
                                            filters={analysisFilters} // FIXED: Pass filters to InspectionTable
                                        />

                                        {/* PAGINATION CONTROLS */}
                                        {inspectionPagination && inspectionPagination.totalPages > 1 && (
                                            <div className="card mt-3 border-0 bg-light">
                                                <div className="card-body py-2">
                                                    <div className="d-flex justify-content-between align-items-center">
                                                        <div className="small text-muted">
                                                            Page {inspectionPagination.page} of {inspectionPagination.totalPages} ({inspectionPagination.total.toLocaleString()} total)
                                                        </div>
                                                        <nav>
                                                            <ul className="pagination pagination-sm mb-0">
                                                                <li className={`page-item ${inspectionPagination.page === 1 ? 'disabled' : ''}`}>
                                                                    <button className="page-link" onClick={() => onInspectionPageChange(1)} disabled={inspectionPagination.page === 1}>«</button>
                                                                </li>
                                                                <li className={`page-item ${inspectionPagination.page === 1 ? 'disabled' : ''}`}>
                                                                    <button className="page-link" onClick={() => onInspectionPageChange(inspectionPagination.page - 1)} disabled={inspectionPagination.page === 1}>‹</button>
                                                                </li>
                                                                <li className="page-item active"><span className="page-link">{inspectionPagination.page}</span></li>
                                                                <li className={`page-item ${inspectionPagination.page >= inspectionPagination.totalPages ? 'disabled' : ''}`}>
                                                                    <button className="page-link" onClick={() => onInspectionPageChange(inspectionPagination.page + 1)} disabled={inspectionPagination.page >= inspectionPagination.totalPages}>›</button>
                                                                </li>
                                                                <li className={`page-item ${inspectionPagination.page >= inspectionPagination.totalPages ? 'disabled' : ''}`}>
                                                                    <button className="page-link" onClick={() => onInspectionPageChange(inspectionPagination.totalPages)} disabled={inspectionPagination.page >= inspectionPagination.totalPages}>»</button>
                                                                </li>
                                                            </ul>
                                                        </nav>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                            <i className="fas fa-times me-2"></i>Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};